import React, { useState, useEffect } from 'react';
import './HostCard.css';
import { getUserProfile } from '../utils/firestoreUser';
import PreviewProfileModal from './PreviewProfileModal';

import ReactCountryFlag from 'react-country-flag';
import { countryToAlpha2 } from 'country-to-iso';
import { FiMaximize } from 'react-icons/fi';

// Hook לזיהוי מובייל
function useIsMobile(breakpoint = 600) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);
    return isMobile;
}

// Simple Flag component (can be shared or improved)
const Flag = ({ code }) => {
    const flags = {
        FR: '🇫🇷',
        DE: '🇩🇪',
        IT: '🇮🇹',
        US: '🇺🇸'
        // Add more flags
    };
    return <span>{flags[code] || code}</span>; // Show code if flag not found
};

function HostCard({ dinner, onRequestJoin, isRequestSent, onImageClick, onProfileClick, onCardClick }) {
    const [hostProfile, setHostProfile] = useState(null);
    const [showProfilePreview, setShowProfilePreview] = useState(false);
    const isMobile = useIsMobile(600);

    useEffect(() => {
        async function fetchHostProfile() {
            if (dinner.hostId) {
                const profile = await getUserProfile(dinner.hostId);
                setHostProfile(profile);
            }
        }
        fetchHostProfile();
    }, [dinner.hostId]);

    // Handle profile click to show PreviewProfileModal
    const handleProfileClick = (e) => {
        e.stopPropagation(); // Prevent card click from firing
        if (hostProfile) {
            onProfileClick(dinner); // Let Home.jsx handle the modal
        }
    };

    // Defensive checks
    const address = dinner.address || {};
    const dinnerDetails = dinner.dinnerDetails || {};
    
    // Debug log for address data
    console.log('HostCard address data:', address);
    console.log('HostCard country:', address.country);

    // Format the dates
    const formatDates = () => {
        // Handle recurring option
        if (dinnerDetails.recurringOption) {
            const frequencyMap = {
                'weekly': 'Every week',
                'biweekly': 'Every two weeks',
                'triweekly': 'Every three weeks',
                'monthly': 'Every month'
            };
            return `${frequencyMap[dinnerDetails.recurringOption]}`;
        }

        // Handle non-recurring dates
        if (!Array.isArray(dinnerDetails.availableDates) || dinnerDetails.availableDates.length === 0) {
            return 'No dates available';
        }

        // Format each date and join them with commas
        return dinnerDetails.availableDates
            .map(date => {
                try {
                    const dateObj = new Date(date);
                    if (isNaN(dateObj.getTime())) {
                        return '';
                    }
                    return dateObj.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                } catch (error) {
                    console.error('Error formatting date:', error);
                    return '';
                }
            })
            .filter(date => date !== '') // Remove any invalid dates
            .join(', ');
    };

    // Format the location string
    const location = [
        address.city,
        address.country
    ].filter(Boolean).join(', ');

    // Get country code from country name
    const getCountryCodeSafe = (country) => {
        if (!country) return '';
        
        // מיפוי ידני לשמות מדינות בעייתיים
        const countryNameMap = {
            'United States of America': 'US',
            'USA': 'US',
            'United Kingdom': 'GB',
            'UK': 'GB',
            'Czechia': 'CZ',
            'Czech Republic': 'CZ'
        };
        
        const normalized = country.trim();
        if (countryNameMap[normalized]) return countryNameMap[normalized];
        
        const code = countryToAlpha2(normalized);
        console.log('Country:', country, 'Code:', code); // Debug log
        return code || '';
    };

    // --- מובייל: תצוגה קומפקטית ---
    if (isMobile) {
        return (
            <div className="host-card host-card-mobile" onClick={() => onCardClick(dinner)} style={{ cursor: 'pointer' }}>
                <img
                    src={hostProfile?.profileImg || '/blankprofile.png'}
                    alt={hostProfile?.name || 'Host'}
                    className="profile-pic-mobile"
                />
                <div className="host-card-main-mobile">
                    <div className="host-card-title-row-mobile">
                        <span className="host-card-name-mobile">{hostProfile?.name}</span>
                        <span className="host-card-location-mobile">
                            {location}
                            {address.country && (
                                <ReactCountryFlag
                                    countryCode={getCountryCodeSafe(address.country)}
                                    svg
                                    style={{ fontSize: '1em', marginLeft: 6, verticalAlign: 'middle' }}
                                />
                            )}
                        </span>
                    </div>
                    <div className="host-card-dates-row-mobile">
                        <span className="host-card-dates-label-mobile">Available Dates:</span>
                        <span className="host-card-dates-mobile">{formatDates()}</span>
                    </div>
                    {dinner.shabbotAnswers?.funFact && hostProfile?.name && (
                        <div className="fun-fact-host-card-mobile">
      <span>
        <strong>{hostProfile.name}'s fun fact:</strong>
        <span style={{ fontStyle: 'italic', marginLeft: '5px' }}>
          "{dinner.shabbotAnswers.funFact}"
        </span>
      </span>
                        </div>
                    )}
                    <div className="host-card-actions-row-mobile">
                        <button
                            className="host-card-action-mobile"
                            onClick={(e) => {
                                e.stopPropagation();
                                onCardClick(dinner);
                            }}
                        >
                            See More
                        </button>
                        {isRequestSent ? (
                            <button
                                className="host-card-action-mobile sent"
                                disabled
                                onClick={(e) => e.stopPropagation()}
                            >
                                Request Sent
                            </button>
                        ) : (
                            <button
                                className="host-card-action-mobile"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRequestJoin();
                                }}
                            >
                                Request to Join
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // --- דסקטופ: כל המידע ---
    return (
        <div className="host-card" onClick={() => onCardClick(dinner)} style={{ cursor: 'pointer' }}>
            <div className="expand-indicator">
                <FiMaximize />
            </div>
            {/* Profile Picture Section */}
            <div>
                {hostProfile?.profileImg ? (
                    <img
                        src={hostProfile.profileImg || '/blankprofile.png'}
                        alt={`${hostProfile?.name || 'Host'}'s profile`}
                        className="profile-pic"
                        style={{ objectFit: 'cover', objectPosition: 'center', marginLeft: 8, cursor: onImageClick ? 'pointer' : 'default' }}
                        onClick={(e) => { e.stopPropagation(); onImageClick && onImageClick(hostProfile.profileImg || '/blankprofile.png'); }}
                    />
                ) : (
                    <div className="profile-pic host-image-placeholder"></div>
                )}
            </div>

            {/* Content Section */}
            <div className="content-column">
                <div className="host-info">
                    <h3
                        className="host-name"
                        onClick={handleProfileClick}
                        style={{ cursor: 'pointer' }}
                    >
                        {hostProfile?.name}
                    </h3>
                    <div className="location">
                        <span>{[
                            address.neighborhood,
                            address.city,
                            address.country
                        ].filter(Boolean).join(', ')}</span>
                        {address.country && (
                            <ReactCountryFlag
                                countryCode={getCountryCodeSafe(address.country)}
                                svg
                                style={{ fontSize: '1em', marginLeft: 6, verticalAlign: 'middle' }}
                            />
                        )}
                    </div>
                </div>

                <div className="details">
                    {/* Arranged using flexbox for side-by-side display */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5em' }}>
                        <p className="languages mb-0">
                            <strong>We speak:</strong> {hostProfile?.languages || ''}
                        </p>
                        {dinnerDetails.maxGuests && (
                            <p className="max-guests mb-0" style={{ marginLeft: '20px' }}>
                                <strong>Max Guests:</strong> {dinnerDetails.maxGuests}
                            </p>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5em' }}>
                        {dinnerDetails.kosherType && (
                            <p className="kosher-type mb-0">
                                <strong>Kosher Level:</strong> {dinnerDetails.kosherType}
                            </p>
                        )}
                        {dinnerDetails.time && (
                            <p className="dinner-time mb-0" style={{ marginLeft: '20px' }}>
                                <strong>Dinner Time:</strong> {dinnerDetails.time}
                            </p>
                        )}
                    </div>

                    <div className="details-list">
                        {(Array.isArray(dinnerDetails.mealStyle) ? dinnerDetails.mealStyle : []).map(style => (
                            <span key={style} className="meal-style-item">{style}</span>
                        ))}
                    </div>

                    {dinner.shabbotAnswers?.funFact && hostProfile?.name && (
                        <div className="fun-fact-host-card">
                            <span>
                                <strong>{hostProfile.name}'s fun fact:</strong>
                                <span style={{ fontStyle: 'italic', marginLeft: '5px' }}>
                                    "{dinner.shabbotAnswers.funFact}"
                                </span>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="bottom-row">
                <span className="dates">
                    <strong>Dates:</strong> {formatDates()}
                </span>
                {isRequestSent ? (
                    <button className="cta sent" disabled onClick={(e) => e.stopPropagation()}>Request Sent</button>
                ) : (
                    <button className="cta" onClick={(e) => { e.stopPropagation(); onRequestJoin(); }}>Request to join</button>
                )}
            </div>

            {/* This logic is now managed by Home.jsx, but can be kept for other uses if needed */}
            {showProfilePreview && hostProfile && (
                <PreviewProfileModal
                    open={showProfilePreview}
                    onClose={() => setShowProfilePreview(false)}
                    profile={{
                        uid: dinner.hostId, // Pass uid
                        avatar: hostProfile.profileImg,
                        name: hostProfile.name,
                        city: hostProfile.location?.city,
                        country: hostProfile.location?.country,
                        code: getCountryCodeSafe(hostProfile.location?.country),
                        bio: hostProfile.about,
                        languages: hostProfile.languages,
                        interests: hostProfile.interests,
                        gallery: hostProfile.gallery
                    }}
                />
            )}
        </div>
    );
}

export default HostCard;